import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { clientApi, productsApi } from '../services/api';
import { Dropdown } from 'primereact/dropdown';
import { formatAddress } from '../utils/addressUtils';
import {
  DocumentTextIcon,
  FunnelIcon,
  CurrencyDollarIcon,
  UserIcon,
  ArrowDownTrayIcon
} from '@heroicons/react/24/outline';

// CSS для плавной анимации и PrimeReact Dropdown
const slideInAnimation = `
  @keyframes slideInFromTop {
    from {
      opacity: 0;
      transform: translateY(-10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
  
  .slide-in-from-top-2 {
    animation: slideInFromTop 0.3s ease-out;
  }

  /* PrimeReact Dropdown стили для DaisyUI */
  .p-dropdown {
    border: 2px solid hsl(var(--bc) / 0.3) !important;
    border-radius: 0.5rem !important;
    background-color: hsl(var(--b1)) !important;
    color: hsl(var(--bc)) !important;
  }

  .p-dropdown:hover {
    border-color: hsl(var(--bc) / 0.5) !important;
  }

  .p-dropdown:focus-within {
    border-color: hsl(var(--p)) !important;
    box-shadow: 0 0 0 2px hsl(var(--p) / 0.2) !important;
  }

  .p-dropdown-panel {
    background-color: rgb(255 255 255) !important;
    border: 1px solid hsl(var(--bc) / 0.2) !important;
    border-radius: 0.5rem !important;
    box-shadow: 0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1) !important;
  }

  .p-dropdown-item {
    color: hsl(var(--bc)) !important;
    padding: 0.5rem 1rem !important;
    background-color: rgb(255 255 255) !important;
  }

  .p-dropdown-item:hover {
    background-color: rgb(0 0 0 / 0.1) !important;
  }

  .p-dropdown-item.p-highlight {
    background-color: hsl(var(--p)) !important;
    color: hsl(var(--pc)) !important;
  }

  .p-dropdown-trigger {
    color: hsl(var(--bc)) !important;
  }

  .p-dropdown-label {
    color: hsl(var(--bc)) !important;
  }

  .p-dropdown-label.p-inputtext {
    background-color: transparent !important;
  }

  .p-multiselect-header {
    background: #ebebeb !important;
  }

  .p-multiselect-item {
    background: #f1f1f1 !important;
  }
`;

// Добавляем стили в head
if (typeof document !== 'undefined') {
  const styleElement = document.createElement('style');
  styleElement.textContent = slideInAnimation;
  if (!document.head.querySelector('style[data-slide-animation]')) {
    styleElement.setAttribute('data-slide-animation', 'true');
    document.head.appendChild(styleElement);
  }
}

interface Deal {
  id: string;           // Bitrix24 ID сделки (например, '1304865')
  title?: string;       // Может отсутствовать
  ukName?: string;      // Может отсутствовать
  source?: {
    id: string;
    name: string;
  };
  status: string;       // 'active', 'closed', 'failed'
  opportunity: number;  // Число с суммой (например, 500.00)
  createdAt?: Date;
  closedAt?: Date;
  street?: string;
  apartment?: string;
  phone?: string;
  accountNumber?: string;
  comments?: string;
  refusalReason?: string;
  counterType?: string;
  serialNumber?: string;
  readings?: string;
  products?: Array<{
    productId: number;
    productName: string;
    type: string;
    price: number;
    quantity: number;
  }>;
  reward?: {            // Может отсутствовать
    totalReward: number;
    productRewards?: Array<{
      productId: number;
      productName: string;
      productType: string;
      price: number;
      quantity: number;
      reward: number;
      rateType: 'fixed' | 'percentage';
      rate: number;
    }>;
  };
}

interface ClientStats {
  activeDeals: number;      // В работе
  closedDeals: number;      // Завершена
  failedDeals: number;      // Не завершена
  totalReward: number;      // Вознаграждение
}

interface AdminProduct {
  id: string;
  name: string;
  type: 'water' | 'warm' | 'gas' | 'other';
  description?: string;
  isActive: boolean;
  isHidden: boolean;
}

interface ClientProfile {
  id: string;
  login: string;
  ukName: string;
  disableRewards?: boolean;
  showDealId?: boolean;
  showTPlusReport?: boolean;
  sources: Array<{
    id: string;
    name: string;
    statusId: string;
  }>;
}

interface SubmitDealFormState {
  name: string;
  phone: string;
  street: string;
  house: string;
  apartment: string;
  comment: string;
}

const createEmptySubmitForm = (): SubmitDealFormState => ({
  name: '',
  phone: '',
  street: '',
  house: '',
  apartment: '',
  comment: ''
});

const ClientPanel: React.FC = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportingTPlus, setExportingTPlus] = useState(false);
  const [deals, setDeals] = useState<Deal[]>([]);
  const [stats, setStats] = useState<ClientStats>({
    activeDeals: 0,
    closedDeals: 0,
    failedDeals: 0,
    totalReward: 0,
  });
  const [, setAdminProducts] = useState<AdminProduct[]>([]);
  const [adminProductsMap, setAdminProductsMap] = useState<Map<string, AdminProduct>>(new Map());
  const [adminProductsLoading, setAdminProductsLoading] = useState(false);
  const [adminProductsLoadingPromise, setAdminProductsLoadingPromise] = useState<Promise<void> | null>(null);
  const [profile, setProfile] = useState<ClientProfile | null>(null);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedStage, setSelectedStage] = useState<string>('closed');
  const [selectedDateType, setSelectedDateType] = useState<'created' | 'closed'>('closed');

  // State for expanded deals
  const [expandedDeals, setExpandedDeals] = useState<Set<string>>(new Set());

  // State for fullscreen gallery
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [currentDealPictures, setCurrentDealPictures] = useState<any[]>([]);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [panPosition, setPanPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });

  // State for loaded pictures (ленивая загрузка)
  const [loadedPictures, setLoadedPictures] = useState<Map<string, any[]>>(new Map());
  const [loadingPictures, setLoadingPictures] = useState<Set<string>>(new Set());

  // Кэш для сделок (чтобы не перезагружать при смене фильтров)
  const [dealsCache, setDealsCache] = useState<Map<string, Deal[]>>(new Map());

  // Состояние для модального окна отправки заявки
  const [submitModalOpen, setSubmitModalOpen] = useState(false);
  const [submitForm, setSubmitForm] = useState<SubmitDealFormState>(() => createEmptySubmitForm());
  const [submitting, setSubmitting] = useState(false);
  const [submitResult, setSubmitResult] = useState<{
    success: boolean;
    message: string;
  } | null>(null);

  // Обработчик горячих клавиш и колесика мыши для галереи
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!galleryOpen) return;

      switch (e.key) {
        case 'Escape':
          setGalleryOpen(false);
          break;
        case 'ArrowLeft':
          e.preventDefault();
          if (currentDealPictures.length > 1) {
            setCurrentImageIndex(prev =>
              prev === 0 ? currentDealPictures.length - 1 : prev - 1
            );
            setZoomLevel(1);
            setPanPosition({ x: 0, y: 0 });
          }
          break;
        case 'ArrowRight':
          e.preventDefault();
          if (currentDealPictures.length > 1) {
            setCurrentImageIndex(prev =>
              prev === currentDealPictures.length - 1 ? 0 : prev + 1
            );
            setZoomLevel(1);
            setPanPosition({ x: 0, y: 0 });
          }
          break;
        case '+':
        case '=':
          e.preventDefault();
          setZoomLevel(prev => Math.min(3, prev + 0.25));
          break;
        case '-':
          e.preventDefault();
          setZoomLevel(prev => Math.max(0.5, prev - 0.25));
          break;
        case '0':
          e.preventDefault();
          setZoomLevel(1);
          setPanPosition({ x: 0, y: 0 });
          break;
      }
    };

    const handleWheel = (e: WheelEvent) => {
      if (!galleryOpen) return;

      e.preventDefault();
      if (e.deltaY < 0) {
        setZoomLevel(prev => Math.min(3, prev + 0.1));
      } else {
        setZoomLevel(prev => Math.max(0.5, prev - 0.1));
      }
    };

    // Блокируем скролл страницы когда галерея открыта
    if (galleryOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }

    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('wheel', handleWheel, { passive: false });

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('wheel', handleWheel);
      // Восстанавливаем скролл при закрытии галереи
      document.body.style.overflow = 'auto';
    };
  }, [galleryOpen, currentDealPictures.length]);

  // Проверяем права доступа
  if (user?.type !== 'client') {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-error">Доступ запрещен</h2>
          <p className="text-base-content/70">У вас нет прав для доступа к этой странице</p>
        </div>
      </div>
    );
  }

  // Загружаем профиль клиента и сделки при первом входе
  useEffect(() => {
  const initializeData = async () => {
    // Сначала загружаем товары, потом профиль и сделки
    await loadAdminProducts();
    await Promise.all([
      loadProfile(),
    ]);
    // Автоматически загружаем сделки за текущий месяц и год
    await loadDeals();
  };
    initializeData();
  }, []);

  // Загружаем сделки при изменении фильтров (только если профиль уже загружен)
  useEffect(() => {
    if (profile) { // Только если профиль загружен
      loadDeals();
    }
  }, [selectedMonth, selectedYear, selectedStage, selectedDateType]);

  // Загружаем статистику при изменении сделок (НЕ вызываем API!)
  useEffect(() => {
    // Рассчитываем статистику локально из уже загруженных сделок
    // Даже если сделок нет (длина 0), нужно обнулить статистику
    calculateStatsLocally(deals);
  }, [deals]);

  useEffect(() => {
    if (!deals.length) return;

    deals.forEach((deal) => {
      if (String(deal.id) === '1380517') {
        console.debug('[ClientPanel][DealSumDebug]', {
          dealId: deal.id,
          title: deal.title,
          opportunity: deal.opportunity,
          reward: deal.reward,
          products: deal.products,
        });
      }
    });
  }, [deals]);

  const loadProfile = async () => {
    try {
      const response = await clientApi.getClientProfile();
      setProfile(response.data);
    } catch (error) {
      console.error('Ошибка загрузки профиля:', error);
    }
  };

  const loadAdminProducts = async () => {
    // Если уже загружается, возвращаем существующий промис
    if (adminProductsLoadingPromise) {
      return adminProductsLoadingPromise;
    }
    
    // Если уже загружено, не загружаем повторно
    if (adminProductsMap.size > 0) {
      return Promise.resolve();
    }
    
    setAdminProductsLoading(true);
    const promise = (async () => {
      try {
        const response = await productsApi.getProducts();
        const products = response as AdminProduct[];
        
        setAdminProducts(products || []);
        
        // Создаём Map для быстрого поиска по имени товара
        // API уже отфильтровал товары (isActive: true, isHidden: false, hiddenForClientIds)
        const productsMap = new Map<string, AdminProduct>();
        if (products && products.length > 0) {
          products.forEach(product => {
            // Нормализуем название: убираем лишние пробелы и приводим к нижнему регистру
            const normalizedName = product.name.trim().toLowerCase().replace(/\s+/g, ' ');
            productsMap.set(normalizedName, product);
            // Также добавляем оригинальное название на случай точного совпадения
            productsMap.set(product.name.toLowerCase(), product);
          });
        }
        setAdminProductsMap(productsMap);
      } catch (error) {
        // Ошибка загрузки товаров
      } finally {
        setAdminProductsLoading(false);
        setAdminProductsLoadingPromise(null);
      }
    })();
    
    setAdminProductsLoadingPromise(promise);
    return promise;
  };

  const getCacheKey = () => {
    return `${selectedMonth}-${selectedYear}-${selectedStage}-${selectedDateType}`;
  };

  const loadDeals = async (forceRefresh = false) => {
    const cacheKey = getCacheKey();

    // Проверяем кэш только если не принудительное обновление
    if (!forceRefresh && dealsCache.has(cacheKey)) {
      setDeals(dealsCache.get(cacheKey)!);
      return;
    }

    setLoading(true);
    try {
      // Ждем загрузки товаров, если они еще не загружены
      if (adminProductsMap.size === 0 && !adminProductsLoading) {
        await loadAdminProducts();
      } else if (adminProductsLoading) {
        await adminProductsLoadingPromise;
      }

      const filters = {
        month: selectedMonth,
        year: selectedYear,
        stageId: selectedStage || undefined,
        dateType: selectedDateType,
      };

      const response = await clientApi.getClientDeals(filters);
      const dealsData = response.data?.deals || [];

      // Сохраняем в кэш
      setDealsCache(prev => new Map(prev).set(cacheKey, dealsData));
      setDeals(dealsData);
    } catch (error) {
      console.error('Ошибка загрузки сделок:', error);
      setDeals([]);
    } finally {
      setLoading(false);
    }
  };

  const exportToExcel = async () => {
    setExporting(true);
    try {
      const filters = {
        month: selectedMonth,
        year: selectedYear,
        stageId: selectedStage || undefined,
        dateType: selectedDateType,
      };

      const blob = await clientApi.exportClientDealsToExcel(filters);

      // Создаем ссылку для скачивания
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `client_deals_${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Ошибка экспорта в Excel:', error);
      alert('Ошибка при экспорте файла. Попробуйте еще раз.');
    } finally {
      setExporting(false);
    }
  };

  const exportToTPlusReport = async () => {
    setExportingTPlus(true);
    try {
      const filters = {
        month: selectedMonth,
        year: selectedYear,
        stageId: selectedStage || undefined,
        dateType: selectedDateType,
      };

      const blob = await clientApi.exportClientDealsToTPlusReport(filters);

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `tplus_report_${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Ошибка выгрузки отчета Т-Плюс:', error);
      alert('Ошибка при выгрузке отчета. Попробуйте еще раз.');
    } finally {
      setExportingTPlus(false);
    }
  };

  // Функция для форматирования телефона
  const formatPhone = (value: string) => {
    const phoneNumber = value.replace(/\D/g, '');
    if (phoneNumber.length === 0) return '';
    if (phoneNumber.length === 1) return `+7 (`;
    if (phoneNumber.length <= 4) return `+7 (${phoneNumber.slice(1)}`;
    if (phoneNumber.length <= 7) return `+7 (${phoneNumber.slice(1, 4)}) ${phoneNumber.slice(4)}`;
    if (phoneNumber.length === 8) return `+7 (${phoneNumber.slice(1, 4)}) ${phoneNumber.slice(4, 7)}-${phoneNumber.slice(7)}`;
    if (phoneNumber.length === 9) return `+7 (${phoneNumber.slice(1, 4)}) ${phoneNumber.slice(4, 7)}-${phoneNumber.slice(7, 9)}-${phoneNumber.slice(9, 10)}`;
    if (phoneNumber.length === 10) return `+7 (${phoneNumber.slice(1, 4)}) ${phoneNumber.slice(4, 7)}-${phoneNumber.slice(7, 9)}-${phoneNumber.slice(9, 11)}`;
    if (phoneNumber.length === 11) return `+7 (${phoneNumber.slice(1, 4)}) ${phoneNumber.slice(4, 7)}-${phoneNumber.slice(7, 9)}-${phoneNumber.slice(9, 11)}`;
    // Ограничиваем до 11 цифр
    return `+7 (${phoneNumber.slice(1, 4)}) ${phoneNumber.slice(4, 7)}-${phoneNumber.slice(7, 9)}-${phoneNumber.slice(9, 11)}`;
  };

  // Обработчик изменения телефона (для мобильных устройств)
  const handlePhoneChange = (value: string) => {
    // Получаем только цифры из введенного значения
    const digitsOnly = value.replace(/\D/g, '');
    
    // Ограничиваем до 11 цифр (7 + 10 цифр номера)
    const limitedDigits = digitsOnly.slice(0, 11);
    
    // Форматируем
    const formatted = formatPhone(limitedDigits);
    setSubmitForm(prev => ({ ...prev, phone: formatted }));
  };

  // Обработчик для мобильных устройств (onInput)
  const handlePhoneInput = (e: React.FormEvent<HTMLInputElement>) => {
    const value = e.currentTarget.value;
    handlePhoneChange(value);
  };

  // Обработчик нажатия клавиш для телефона
  const handlePhoneKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // Если нажат Backspace или Delete
    if (e.key === 'Backspace' || e.key === 'Delete') {
      const currentPhone = submitForm.phone;
      const digitsOnly = currentPhone.replace(/\D/g, '');
      
      // Если есть цифры для стирания
      if (digitsOnly.length > 1) {
        // Убираем последнюю цифру
        const newDigits = digitsOnly.slice(0, -1);
        const formatted = formatPhone(newDigits);
        setSubmitForm(prev => ({ ...prev, phone: formatted }));
        e.preventDefault(); // Предотвращаем стандартное поведение
      } else {
        // Если остается только "7", очищаем поле
        setSubmitForm(prev => ({ ...prev, phone: '' }));
        e.preventDefault();
      }
    }
  };

  // Обработчик отправки заявки
  const handleSubmitDeal = async () => {
    const trimmedName = submitForm.name.trim();
    const trimmedStreet = submitForm.street.trim();
    const trimmedHouse = submitForm.house.trim();
    const trimmedApartment = submitForm.apartment.trim();
    const trimmedComment = submitForm.comment.trim();

    if (!trimmedName || !submitForm.phone || !trimmedStreet || !trimmedHouse) {
      setSubmitResult({
        success: false,
        message: 'Пожалуйста, заполните обязательные поля'
      });
      return;
    }

    setSubmitting(true);
    setSubmitResult(null);
    
    try {
      const result = await clientApi.submitDeal({
        name: trimmedName,
        phone: submitForm.phone,
        street: trimmedStreet,
        house: trimmedHouse,
        apartment: trimmedApartment || undefined,
        comment: trimmedComment || undefined
      });

      if (result.success) {
        setSubmitResult({
          success: true,
          message: 'Заявка успешно отправлена! Список сделок обновлен.'
        });
        
        // Автоматически обновляем список сделок (принудительно, без кеша)
        await loadDeals(true);
        
        // Очищаем форму через 3 секунды (больше времени для чтения)
        setTimeout(() => {
          setSubmitModalOpen(false);
          setSubmitForm(createEmptySubmitForm());
          setSubmitResult(null);
        }, 3000);
      } else {
        setSubmitResult({
          success: false,
          message: result.message || 'Произошла ошибка при отправке заявки'
        });
      }
    } catch (error) {
      console.error('Ошибка отправки заявки:', error);
      setSubmitResult({
        success: false,
        message: 'Произошла ошибка при отправке заявки'
      });
    } finally {
      setSubmitting(false);
    }
  };

  // Функция для закрытия модального окна
  const handleCloseModal = async () => {
    setSubmitModalOpen(false);
    setSubmitForm(createEmptySubmitForm());
    setSubmitResult(null);
    setSubmitting(false);
    
    // Принудительно обновляем список сделок при закрытии
    await loadDeals(true);
  };

  const calculateStatsLocally = (dealsData: Deal[]) => {
    if (!dealsData || !Array.isArray(dealsData)) {
      setStats({
        activeDeals: 0,
        closedDeals: 0,
        failedDeals: 0,
        totalReward: 0,
      });
      return;
    }

    let activeDeals = 0;
    let closedDeals = 0;
    let failedDeals = 0;
    let totalReward = 0;

    dealsData.forEach((deal) => {
      // Подсчет по стадиям
      if (deal.status === 'active') {
        activeDeals++;
      } else if (deal.status === 'closed') {
        closedDeals++;
        // Вознаграждение только по завершенным сделкам
        const dealReward = deal.reward?.totalReward || 0;
        totalReward += dealReward;
      } else if (deal.status === 'failed') {
        failedDeals++;
      }
    });

    setStats({
      activeDeals,
      closedDeals,
      failedDeals,
      totalReward,
    });
  };

  const months = [
    1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12
  ];

  const years = Array.from({ length: 2 }, (_, i) => new Date().getFullYear() - i);

  const stages = [
    '', 'active', 'closed', 'failed'
  ];

  const getMonthName = (month: number) => {
    const monthNames = [
      'Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь',
      'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь'
    ];
    return monthNames[month - 1];
  };

  const getStageName = (stage: string) => {
    switch (stage) {
      case '':
        return 'Все стадии';
      case 'active':
        return 'В работе';
      case 'closed':
        return 'Завершена';
      case 'failed':
        return 'Не завершена';
      default:
        return stage;
    }
  };

  const getStageColor = (stage: string) => {
    switch (stage) {
      case 'closed': return 'text-success';
      case 'failed': return 'text-error';
      case 'active': return 'text-info';
      default: return 'text-base-content';
    }
  };

  const getStageDisplayName = (stage: string) => {
    switch (stage) {
      case 'closed':
        return 'Завершена';
      case 'failed':
        return 'Не завершена';
      case 'active':
        return 'В работе';
      default:
        // Если стадия не распознана, показываем оригинальное значение
        return stage;
    }
  };



  const getProductTypeDisplayName = (type: string) => {
    switch (type) {
      case 'water':
        return 'Вода';
      case 'warm':
        return 'Тепло';
      case 'gas':
        return 'Газ';
      case 'other':
        return 'Другое';
      default:
        return type;
    }
  };

  const getProductTypeColor = (type: string) => {
    switch (type) {
      case 'water': return 'text-info';
      case 'warm': return 'text-warning';
      case 'gas': return 'text-error';
      default: return 'text-base-content';
    }
  };

  // Функция для получения информации о товаре из AdminProduct
  const getProductInfo = (productName: string | undefined, productType?: string) => {
    if (!productName) {
      return {
        type: 'Неизвестный товар',
        description: '-',
        typeColor: 'text-base-content/50',
      };
    }
    
    // Если тип товара пришел с бэкенда, используем его
    if (productType && productType !== 'other') {
      // Ищем товар в Map по названию (пробуем разные варианты)
      let adminProduct = adminProductsMap.get(productName.toLowerCase());
      if (!adminProduct) {
        // Пробуем нормализованное название
        const normalized = productName.trim().toLowerCase().replace(/\s+/g, ' ');
        adminProduct = adminProductsMap.get(normalized);
      }
      
      return {
        type: getProductTypeDisplayName(productType as 'water' | 'warm' | 'gas' | 'other'),
        description: adminProduct?.description || productName,
        typeColor: getProductTypeColor(productType as 'water' | 'warm' | 'gas' | 'other'),
      };
    }
    
    // Иначе ищем в adminProductsMap
    let adminProduct = adminProductsMap.get(productName.toLowerCase());
    if (!adminProduct) {
      const normalized = productName.trim().toLowerCase().replace(/\s+/g, ' ');
      adminProduct = adminProductsMap.get(normalized);
    }
    if (adminProduct) {
      return {
        type: getProductTypeDisplayName(adminProduct.type),
        description: adminProduct.description || '-',
        typeColor: getProductTypeColor(adminProduct.type),
      };
    }
    return {
      type: 'Неизвестный товар',
      description: productName,
      typeColor: 'text-base-content/50',
    };
  };

  // Функция для получения товаров сделки
  const getDealProducts = (deal: Deal) => {
    if (deal.products && deal.products.length > 0) {
      return deal.products;
    }
    return [];
  };

  // Функция для очистки номера телефона (убирает пробелы, дефисы, скобки)
  const cleanPhoneNumber = (phone: string): string => {
    return phone.replace(/[\s\-\(\)]/g, '');
  };

  // Функция для форматирования телефонов (работает с массивом или строкой)
  const formatPhoneNumbers = (phone: string | string[] | undefined | null) => {
    if (!phone) return null;

    // Если это массив - обрабатываем каждый элемент
    if (Array.isArray(phone)) {
      if (phone.length === 0) return null;

      return (
        <div className="text-center space-y-1">
          {phone.map((number, index) => {
            const cleanNumber = cleanPhoneNumber(number.trim());
            if (cleanNumber.length === 0) return null;

            return (
              <div key={index} className="text-xs sm:text-sm font-medium text-base-content">
                {cleanNumber.startsWith('+') ? cleanNumber : `+${cleanNumber}`}
              </div>
            );
          })}
        </div>
      );
    }

    // Если это строка - обрабатываем как раньше
    if (typeof phone === 'string') {
      // Разделяем по символу + и фильтруем пустые строки
      let numbers = phone.split('+').filter(num => num.trim().length > 0);

      // Если первый номер не начинается с +, но содержит цифры, добавляем его
      if (numbers.length > 0 && !phone.startsWith('+')) {
        // Первый элемент может содержать номер без +
        const firstNumber = numbers[0];
        if (firstNumber.trim().length > 0) {
          numbers = [firstNumber, ...numbers.slice(1)];
        }
      }

      if (numbers.length === 0) return null;

      return (
        <div className="text-center space-y-1">
          {numbers.map((number, index) => {
            const cleanNumber = cleanPhoneNumber(number.trim());
            if (cleanNumber.length === 0) return null;

            return (
              <div key={index} className="text-xs sm:text-sm font-medium text-base-content">
                {cleanNumber.startsWith('+') ? cleanNumber : `+${cleanNumber}`}
              </div>
            );
          })}
        </div>
      );
    }

    return null;
  };

  const toggleDealExpansion = (dealId: string) => {
    setExpandedDeals(prev => {
      const newSet = new Set(prev);
      if (newSet.has(dealId)) {
        newSet.delete(dealId);
      } else {
        newSet.add(dealId);
        loadDealPictures(dealId, true);
      }
      return newSet;
    });
  };

  const loadDealPictures = async (dealId: string, force = false) => {
    if (!force && loadedPictures.has(dealId)) {
      return;
    }

    // Если уже загружаем, не начинаем повторно
    if (loadingPictures.has(dealId)) {
      return;
    }

    setLoadingPictures(prev => new Set(prev).add(dealId));

    try {
      const fetchPictures = async (url: string, options?: RequestInit) => {
        const response = await fetch(url, options);
        if (!response.ok) {
          throw new Error('Не удалось загрузить картинки');
        }
        return response.json();
      };

      let pictures = await fetchPictures(`/api/v1/pictures/deal/${dealId}`);

      if ((!pictures || pictures.length === 0) && force) {
        pictures = await fetchPictures(`/api/v1/pictures/deal/${dealId}/sync`, {
          method: 'POST',
        });
      }

      setLoadedPictures(prev => new Map(prev).set(dealId, pictures || []));
    } catch (error) {
      // Игнорируем ошибки загрузки картинок
    } finally {
      setLoadingPictures(prev => {
        const newSet = new Set(prev);
        newSet.delete(dealId);
        return newSet;
      });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="loading loading-spinner loading-lg text-primary mb-4"></div>
          <p className="text-base-content/70">Загружаем данные...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Информация о пользователе */}
      <div className="card bg-gradient-to-r from-primary/10 to-secondary/10 shadow-sm">
        <div className="card-body p-6">
          <div className="flex items-center space-x-4">
            <div className="avatar placeholder">
              <div className="bg-primary text-primary-content rounded-full w-12">
                <UserIcon className="w-6 h-6" />
              </div>
            </div>
            <div>
              <h3 className="text-lg font-medium text-base-content">
                {profile?.login || user?.login}
              </h3>
            </div>
          </div>
        </div>
      </div>

      {/* Фильтры */}
      <div className="card bg-base-100 shadow-sm border border-base-300">
        <div className="card-body p-6">
          <h3 className="card-title text-lg font-bold text-base-content flex items-center gap-2 mb-6">
            <FunnelIcon className="w-6 h-6" />
            Фильтры
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
            {/* Месяц */}
            <div className="form-control">
              <div className="border-2 border-base-300 rounded-lg">
                <Dropdown
                  value={selectedMonth}
                  options={months}
                  onChange={(e) => setSelectedMonth(Number(e.value))}
                  placeholder="Выберите месяц"
                  className="w-full"
                  itemTemplate={(option) => getMonthName(option)}
                  valueTemplate={(option) => getMonthName(option)}
                />
              </div>
            </div>

            {/* Год */}
            <div className="form-control">
              <div className="border-2 border-base-300 rounded-lg">
                <Dropdown
                  value={selectedYear}
                  options={years}
                  onChange={(e) => setSelectedYear(Number(e.value))}
                  placeholder="Выберите год"
                  className="w-full"
                />
              </div>
            </div>

            {/* Стадия */}
            <div className="form-control">
              <div className="border-2 border-base-300 rounded-lg">
                <Dropdown
                  value={selectedStage}
                  options={stages}
                  onChange={(e) => setSelectedStage(e.value)}
                  placeholder="Выберите стадию"
                  className="w-full"
                  itemTemplate={(option) => getStageName(option)}
                  valueTemplate={(option) => getStageName(option)}
                />
              </div>
            </div>

            {/* Тип даты */}
            <div className="form-control">
              <div className="border-2 border-base-300 rounded-lg">
                <Dropdown
                  value={selectedDateType}
                  options={[
                    { label: 'Дата создания', value: 'created' },
                    { label: 'Дата завершения', value: 'closed' }
                  ]}
                  onChange={(e) => setSelectedDateType(e.value)}
                  placeholder="Выберите тип даты"
                  className="w-full"
                  optionLabel="label"
                  optionValue="value"
                />
              </div>
            </div>
          </div>

          {/* Кнопки действий */}
          <div className="flex flex-col sm:flex-row items-center gap-4 justify-center">
            <button
              onClick={() => loadDeals()}
              disabled={loading}
              className="btn btn-primary w-full sm:w-auto btn-lg px-6 sm:px-8 py-3 sm:py-4 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <span className="loading loading-spinner loading-md"></span>
              ) : (
                <>
                  <span className="mr-2 text-lg">📊</span>
                  <span className="text-base font-semibold">Загрузить сделки</span>
                </>
              )}
            </button>

            <button
              onClick={() => {
                setSelectedMonth(new Date().getMonth() + 1);
                setSelectedYear(new Date().getFullYear());
                setSelectedStage('closed');
                setSelectedDateType('closed');
              }}
              className="btn btn-outline w-full sm:w-auto btn-lg px-4 sm:px-6 py-3 sm:py-4"
            >
              <span className="mr-2 text-lg">🔄</span>
              <span className="text-base font-semibold">Сбросить фильтры</span>
            </button>

            <button
              onClick={exportToExcel}
              disabled={exporting || !deals || deals.length === 0}
              className="btn btn-success w-full sm:w-auto btn-lg px-6 sm:px-8 py-3 sm:py-4 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {exporting ? (
                <span className="loading loading-spinner loading-md"></span>
              ) : (
                <>
                  <ArrowDownTrayIcon className="w-5 h-5 mr-2" />
                  <span className="text-base font-semibold">Excel</span>
                </>
              )}
            </button>

            {profile?.showTPlusReport && (
              <button
                onClick={exportToTPlusReport}
                disabled={exportingTPlus || !deals || deals.length === 0}
                className="btn btn-secondary w-full sm:w-auto btn-lg px-6 sm:px-8 py-3 sm:py-4 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {exportingTPlus ? (
                  <span className="loading loading-spinner loading-md"></span>
                ) : (
                  <span className="text-base font-semibold">Отчёт</span>
                )}
              </button>
            )}

            <button
              onClick={() => setSubmitModalOpen(true)}
              className="btn btn-info w-full sm:w-auto btn-lg px-6 sm:px-8 py-3 sm:py-4 ml-4"
            >
              <span className="mr-2 text-lg">📝</span>
              <span className="text-base font-semibold">Отправить заявку</span>
            </button>
          </div>
        </div>
      </div>

      {/* Статистика */}
      <div className="card bg-base-100 shadow-sm p-6 mb-6">
        <h2 className="text-2xl font-bold text-base-content mb-6 flex items-center gap-3">
          <span className="text-2xl">📊</span>
          Статистика
        </h2>
        <p className="text-sm text-base-content/70 mb-4 text-center">
          💡 Вознаграждение рассчитывается только по завершенным сделкам
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="stat bg-base-200/30 rounded-lg">
            <div className="stat-figure text-info">
              <DocumentTextIcon className="h-8 w-8" />
            </div>
            <div className="stat-title">В работе</div>
            <div className="stat-value text-info">{stats.activeDeals}</div>
          </div>

          <div className="stat bg-base-200/30 rounded-lg">
            <div className="stat-figure text-success">
              <DocumentTextIcon className="h-8 w-8" />
            </div>
            <div className="stat-title">Завершена</div>
            <div className="stat-value text-success">{stats.closedDeals}</div>
          </div>

          <div className="stat bg-base-200/30 rounded-lg">
            <div className="stat-figure text-error">
              <DocumentTextIcon className="h-8 w-8" />
            </div>
            <div className="stat-title">Не завершена</div>
            <div className="stat-value text-error">{stats.failedDeals}</div>
          </div>

          {!profile?.disableRewards && (
            <div className="stat bg-base-200/30 rounded-lg">
              <div className="stat-figure text-warning">
                <CurrencyDollarIcon className="h-8 w-8" />
              </div>
              <div className="stat-title">Вознаграждение</div>
              <div className="stat-value text-warning">{stats.totalReward.toLocaleString()} ₽</div>
            </div>
          )}
        </div>
      </div>

      {/* Таблица сделок */}
      <div className="card bg-base-100 shadow-sm">
        <div className="card-body p-6">
          <h3 className="card-title text-lg font-bold text-base-content flex items-center gap-2">
            <DocumentTextIcon className="w-6 h-6" />
            Сделки
          </h3>
          <p className="text-sm text-base-content/70 mb-4">
            💡 Кликните на строку сделки, чтобы увидеть детальную информацию о товарах. Стадия сделки, адрес и телефон клиента отображаются в соответствующих колонках.
          </p>
          <div className="overflow-x-auto -mx-6 sm:mx-0">
            <div className="min-w-full inline-block align-middle">
              <div className="overflow-hidden">
                <table className="min-w-full divide-y divide-base-200">
                  <thead>
                    <tr className="bg-base-200/50">
                      <th className="text-sm font-bold text-base-content py-3 px-3 sm:px-4 border-b border-base-300 text-center">Дата создания</th>
                      <th className="text-sm font-bold text-base-content py-3 px-3 sm:px-4 border-b border-base-300 text-center">Стадия</th>
                      <th className="text-sm font-bold text-base-content py-3 px-3 sm:px-4 border-b border-base-300 text-center">Дата закрытия</th>
                      <th className="text-sm font-bold text-base-content py-3 px-3 sm:px-4 border-b border-base-300 text-center">Адрес</th>
                      <th className="text-sm font-bold text-base-content py-3 px-3 sm:px-4 border-b border-base-300 text-center">Телефон</th>
                      <th className="text-sm font-bold text-base-content py-3 px-3 sm:px-4 border-b border-base-300 text-center">Тип</th>
                      <th className="text-sm font-bold text-base-content py-3 px-3 sm:px-4 border-b border-base-300 text-center">Описание</th>
                      <th className="text-sm font-bold text-base-content py-3 px-3 sm:px-4 border-b border-base-300 text-center">Кол-во</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-base-200">
                    {deals && Array.isArray(deals) && deals.flatMap((deal) => {
                      const products = getDealProducts(deal);
                      const rows: JSX.Element[] = [];
                      
                      if (products.length === 0) {
                        // Если товаров нет, показываем одну строку с "Нет товаров"
                        rows.push(
                          <React.Fragment key={deal.id}>
                            <tr
                              className={`cursor-pointer transition-all duration-200 group ${expandedDeals.has(deal.id)
                                ? 'bg-primary/5 border-l-4 border-l-primary shadow-md'
                                : 'hover:bg-base-100 hover:shadow-sm'
                                }`}
                              onClick={() => toggleDealExpansion(deal.id)}
                            >
                              <td className="font-medium text-xs sm:text-sm text-base-content py-3 px-3 sm:px-4 group-hover:text-primary transition-colors text-center">
                                {deal.createdAt ? new Date(deal.createdAt).toLocaleDateString() : '-'}
                              </td>

                              <td className="py-3 px-3 sm:px-4 text-center">
                                <span className={`${getStageColor(deal.status)} font-medium text-xs sm:text-sm`}>
                                  {getStageDisplayName(deal.status)}
                                </span>
                              </td>

                              <td className="font-medium text-xs sm:text-sm text-base-content py-3 px-3 sm:px-4 group-hover:text-primary transition-colors text-center">
                                {deal.closedAt ? new Date(deal.closedAt).toLocaleDateString() : '-'}
                              </td>

                              <td className="py-3 px-3 sm:px-4 text-center max-w-[180px]">
                                {deal.street ? (
                                  <div className="text-xs sm:text-sm text-center font-medium text-base-content">
                                    {formatAddress(`${deal.street}${deal.apartment ? ` кв.${deal.apartment}` : ''}`)}
                                  </div>
                                ) : (
                                  <span className="text-base-content/50 italic text-xs sm:text-sm">Не указан</span>
                                )}
                              </td>

                              <td className="py-3 px-3 sm:px-4 text-center">
                                {deal.phone ? (
                                  formatPhoneNumbers(deal.phone)
                                ) : (
                                  <span className="text-base-content/50 italic">Не указан</span>
                                )}
                              </td>

                              <td className="py-3 px-3 sm:px-4 text-center" colSpan={3}>
                                <span className="text-base-content/50 italic text-xs sm:text-sm">
                                  Нет товаров
                                  {expandedDeals.has(deal.id) && (
                                    <span className="text-primary text-xs animate-pulse ml-2">▼</span>
                                  )}
                                </span>
                              </td>
                            </tr>
                          </React.Fragment>
                        );
                      } else {
                        // Если товары есть, создаём строки для каждого товара
                        products.forEach((product, index) => {
                          const productInfo = getProductInfo(product.productName, product.type);
                          const isFirstRow = index === 0;
                          const isLastRow = index === products.length - 1;
                          
                          rows.push(
                            <React.Fragment key={`${deal.id}-product-${index}`}>
                              <tr
                                className={`cursor-pointer transition-all duration-200 group ${expandedDeals.has(deal.id)
                                  ? `bg-primary/5 border-l-4 border-l-primary ${isLastRow ? 'shadow-md' : ''}`
                                  : 'hover:bg-base-100 hover:shadow-sm'
                                  }`}
                                onClick={() => toggleDealExpansion(deal.id)}
                              >
                                {/* Первая строка товара: показываем информацию о сделке */}
                                {isFirstRow ? (
                                  <>
                                    <td className="font-medium text-xs sm:text-sm text-base-content py-3 px-3 sm:px-4 group-hover:text-primary transition-colors text-center">
                                      {deal.createdAt ? new Date(deal.createdAt).toLocaleDateString() : '-'}
                                    </td>

                                    <td className="py-3 px-3 sm:px-4 text-center">
                                      <span className={`${getStageColor(deal.status)} font-medium text-xs sm:text-sm`}>
                                        {getStageDisplayName(deal.status)}
                                      </span>
                                    </td>

                                    <td className="font-medium text-xs sm:text-sm text-base-content py-3 px-3 sm:px-4 group-hover:text-primary transition-colors text-center">
                                      {deal.closedAt ? new Date(deal.closedAt).toLocaleDateString() : '-'}
                                    </td>

                                    <td className="py-3 px-3 sm:px-4 text-center max-w-[180px]">
                                      {deal.street ? (
                                        <div className="text-xs sm:text-sm text-center font-medium text-base-content">
                                          {formatAddress(`${deal.street}${deal.apartment ? ` кв.${deal.apartment}` : ''}`)}
                                        </div>
                                      ) : (
                                        <span className="text-base-content/50 italic text-xs sm:text-sm">Не указан</span>
                                      )}
                                    </td>

                                    <td className="py-3 px-3 sm:px-4 text-center">
                                      {deal.phone ? (
                                        formatPhoneNumbers(deal.phone)
                                      ) : (
                                        <span className="text-base-content/50 italic">Не указан</span>
                                      )}
                                    </td>
                                  </>
                                ) : (
                                  /* Последующие строки: пустые ячейки */
                                  <>
                                    <td className="py-3 px-3 sm:px-4"></td>
                                    <td className="py-3 px-3 sm:px-4"></td>
                                    <td className="py-3 px-3 sm:px-4"></td>
                                    <td className="py-3 px-3 sm:px-4"></td>
                                    <td className="py-3 px-3 sm:px-4"></td>
                                  </>
                                )}

                                {/* Информация о товаре (для всех строк) */}
                                <td className="py-3 px-3 sm:px-4 text-center">
                                  <span className={`${productInfo.typeColor} font-medium text-xs sm:text-sm`}>
                                    {productInfo.type}
                                  </span>
                                </td>

                                <td className="py-3 px-3 sm:px-4 text-center text-xs sm:text-sm">
                                  {productInfo.description}
                                </td>

                                <td className="py-3 px-3 sm:px-4 text-center">
                                  <span className="bg-base-200 px-2 py-1 rounded-full text-xs font-medium">
                                    {product.quantity}
                                  </span>
                                  {isFirstRow && expandedDeals.has(deal.id) && (
                                    <span className="text-primary text-xs animate-pulse ml-2">▼</span>
                                  )}
                                  {!isFirstRow && expandedDeals.has(deal.id) && (
                                    <span className="ml-1 opacity-0">▼</span>
                                  )}
                                </td>
                              </tr>
                            </React.Fragment>
                          );
                        });
                      }
                      
                      // Добавляем детальную информацию только один раз для сделки (после всех товаров)
                      if (expandedDeals.has(deal.id)) {
                        rows.push(
                            <tr key={`${deal.id}-details`} className="animate-in slide-in-from-top-2 duration-300">
                              <td colSpan={8} className="p-0">
                                <div className="bg-base-200/30 border-t-2 border-primary/20 shadow-lg">
                                  <div className="p-6 relative">
                                    {/* Заголовок с кнопкой закрытия */}
                                    <div className="flex items-center justify-between mb-4">
                                      <h4 className="text-lg font-semibold text-base-content flex items-center gap-2">
                                        <span className="text-primary">📦</span>
                                        Детали сделки
                                      </h4>
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          toggleDealExpansion(deal.id);
                                        }}
                                        className="btn btn-ghost btn-sm btn-circle hover:bg-error/10 hover:text-error transition-all duration-200"
                                        title="Скрыть детали"
                                      >
                                        <span className="text-lg">✕</span>
                                      </button>
                                    </div>

                                    {/* Картинки сделки */}
                                    <div className="mb-6">
                                      <h5 className="text-base font-semibold text-base-content mb-3 flex items-center gap-2">
                                        <span className="text-info">📸</span>
                                        Картинки сделки
                                        {loadingPictures.has(deal.id) && (
                                          <span className="loading loading-spinner loading-xs"></span>
                                        )}
                                      </h5>

                                      {loadingPictures.has(deal.id) ? (
                                        <div className="text-center py-8 text-base-content/50">
                                          <div className="loading loading-spinner loading-lg mb-3"></div>
                                          <p className="text-sm">Загружаем картинки...</p>
                                        </div>
                                      ) : loadedPictures.has(deal.id) && loadedPictures.get(deal.id) && loadedPictures.get(deal.id)!.length > 0 ? (
                                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                                          {loadedPictures.get(deal.id)!.map((picture, index) => (
                                            <div key={picture.id} className="relative group">
                                              <div className="aspect-square bg-base-200 rounded-lg overflow-hidden border border-base-300 shadow-sm hover:shadow-md transition-shadow duration-200">
                                                <img
                                                  src={`/api/v1/pictures/${picture.pictureId}`}
                                                  alt={picture.name || `Картинка ${index + 1}`}
                                                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200 cursor-pointer"
                                                  onClick={() => {
                                                    const pictures = loadedPictures.get(deal.id) || [];
                                                    setCurrentDealPictures(pictures);
                                                    setCurrentImageIndex(index);
                                                    setZoomLevel(1);
                                                    setPanPosition({ x: 0, y: 0 });
                                                    setGalleryOpen(true);
                                                  }}
                                                  onError={(e) => {
                                                    const target = e.target as HTMLImageElement;
                                                    target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgdmlld0JveD0iMCAwIDIwMCAyMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIyMDAiIGhlaWdodD0iMjAwIiBmaWxsPSIjRjNGNEY2Ii8+CjxwYXRoIGQ9Ik02MCA4MEgxNDBWMTIwSDYwVjgwWiIgZmlsbD0iIzlDQTBBNiIvPgo8cGF0aCBkPSJNNjAgNjBIMTQwVjgwSDYwVjYwWiIgZmlsbD0iIzlDQTBBNiIvPgo8L3N2Zz4K';
                                                  }}
                                                />
                                              </div>
                                            </div>
                                          ))}
                                        </div>
                                      ) : (
                                        <div className="text-center py-8 text-base-content/50">
                                          <div className="w-16 h-16 mx-auto bg-base-200 rounded-full flex items-center justify-center mb-3">
                                            <span className="text-2xl">📸</span>
                                          </div>
                                          <p className="text-sm">Картинки не найдены</p>
                                          <p className="text-xs text-base-content/40 mt-1">Картинки будут отображаться здесь, если они есть в CRM</p>
                                        </div>
                                      )}
                                    </div>

                                    {/* Дополнительные поля */}
                                    <div className="mb-6">
                                      <h5 className="text-base font-semibold text-base-content mb-3 flex items-center gap-2">
                                        <span className="text-warning">📋</span>
                                        Детали сделки
                                      </h5>
                                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                        {/* Сумма */}
                                        {!profile?.disableRewards && (
                                          <div className="bg-base-100 border border-base-300 rounded-lg p-3">
                                            <div className="text-xs font-medium text-base-content/70 mb-1">Сумма</div>
                                            <div className="text-sm font-semibold text-base-content">{(deal.opportunity || 0).toLocaleString()} ₽</div>
                                          </div>
                                        )}
                                        
                                        {/* Вознаграждение */}
                                        {!profile?.disableRewards && deal.status === 'closed' && (
                                          <div className="bg-base-100 border border-base-300 rounded-lg p-3">
                                            <div className="text-xs font-medium text-base-content/70 mb-1">Вознаграждение</div>
                                            <div className="text-sm font-semibold text-success">{deal.reward?.totalReward?.toLocaleString() || '0'} ₽</div>
                                          </div>
                                        )}
                                        
                                        {/* ID сделки (опционально) */}
                                        {profile?.showDealId && (
                                          <div className="bg-base-100 border border-base-300 rounded-lg p-3">
                                            <div className="text-xs font-medium text-base-content/70 mb-1">ID сделки</div>
                                            <div className="text-sm font-mono font-medium text-base-content">{deal.id}</div>
                                          </div>
                                        )}
                                        
                                        {deal.accountNumber && (
                                          <div className="bg-base-100 border border-base-300 rounded-lg p-3">
                                            <div className="text-xs font-medium text-base-content/70 mb-1">Номер Л/С</div>
                                            <div className="text-sm font-medium text-base-content">{deal.accountNumber}</div>
                                          </div>
                                        )}
                                        {deal.comments && (
                                          <div className="bg-base-100 border border-base-300 rounded-lg p-3">
                                            <div className="text-xs font-medium text-base-content/70 mb-1">Комментарии</div>
                                            <div className="text-sm font-medium text-base-content">{deal.comments}</div>
                                          </div>
                                        )}
                                        {deal.refusalReason && (
                                          <div className="bg-base-100 border border-base-300 rounded-lg p-3">
                                            <div className="text-xs font-medium text-base-content/70 mb-1">Причина отказа</div>
                                            <div className="text-sm font-medium text-base-content">{deal.refusalReason}</div>
                                          </div>
                                        )}
                                        {deal.counterType && (
                                          <div className="bg-base-100 border border-base-300 rounded-lg p-3">
                                            <div className="text-xs font-medium text-base-content/70 mb-1">Тип счетчика</div>
                                            <div className="text-sm font-medium text-base-content">{deal.counterType}</div>
                                          </div>
                                        )}
                                        {deal.serialNumber && (
                                          <div className="bg-base-100 border border-base-300 rounded-lg p-3">
                                            <div className="text-xs font-medium text-base-content/70 mb-1">Заводской номер</div>
                                            <div className="text-sm font-medium text-base-content">{deal.serialNumber}</div>
                                          </div>
                                        )}
                                        {deal.readings && (
                                          <div className="bg-base-100 border border-base-300 rounded-lg p-3">
                                            <div className="text-xs font-medium text-base-content/70 mb-1">Показания</div>
                                            <div className="text-sm font-medium text-base-content">{deal.readings}</div>
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </td>
                            </tr>
                        );
                      }
                      
                      return rows;
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <p className="text-xs text-base-content/50 mt-4 text-center">
            * Вознаграждение рассчитывается и отображается только для завершенных сделок
          </p>

          {deals.length === 0 && (
            <div className="text-center py-8 sm:py-16 text-base-content/70">
              <div className="w-16 h-16 sm:w-24 sm:h-24 bg-base-200 rounded-full flex items-center justify-center mx-auto mb-4 sm:mb-6">
                <DocumentTextIcon className="w-8 h-8 sm:w-12 sm:h-12 opacity-50" />
              </div>
              <h3 className="text-lg sm:text-xl font-semibold mb-2 sm:mb-3 text-base-content">Сделки не найдены</h3>
              <p className="text-sm sm:text-base mb-4 max-w-md mx-auto px-4">
                По выбранным фильтрам сделки не найдены. Попробуйте изменить параметры поиска или проверьте настройки профиля.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center px-4">
                <button
                  onClick={() => {
                    setSelectedMonth(new Date().getMonth() + 1);
                    setSelectedYear(new Date().getFullYear());
                    setSelectedStage('closed');
                    setSelectedDateType('closed');
                  }}
                  className="btn btn-outline btn-sm w-full sm:w-auto"
                >
                  🔄 Сбросить фильтры
                </button>
                <button
                  onClick={() => loadDeals()}
                  className="btn btn-primary btn-sm w-full sm:w-auto"
                >
                  📊 Попробовать снова
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Полноэкранная галерея картинок */}
      {galleryOpen && currentDealPictures.length > 0 && (
        <div className="fixed inset-0 z-50 bg-black/90 backdrop-blur-sm !mt-0">
          {/* Верхняя панель с кнопками */}
          <div className="absolute top-0 left-0 right-0 z-10 bg-black/50 backdrop-blur-sm border-b border-white/20">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between p-3 gap-4">
              <div className="flex items-center gap-4">
                <h3 className="text-white font-semibold text-base sm:text-lg truncate max-w-[200px] sm:max-w-none">
                  {currentDealPictures[currentImageIndex]?.name || `Картинка ${currentImageIndex + 1}`}
                </h3>
                <span className="text-white/70 text-sm">
                  {currentImageIndex + 1} из {currentDealPictures.length}
                </span>
              </div>

              <div className="flex items-center gap-1 sm:gap-2 flex-wrap">
                {/* Кнопки зума */}
                <button
                  onClick={() => setZoomLevel(prev => Math.max(0.5, prev - 0.25))}
                  className="btn btn-ghost btn-xs sm:btn-sm text-white hover:bg-white/20"
                  disabled={zoomLevel <= 0.5}
                >
                  <span className="hidden sm:inline">🔍-</span>
                  <span className="sm:hidden">-</span>
                </button>
                <span className="text-white/70 text-xs sm:text-sm min-w-[2rem] sm:min-w-[3rem] text-center">
                  {Math.round(zoomLevel * 100)}%
                </span>
                <button
                  onClick={() => setZoomLevel(prev => Math.min(3, prev + 0.25))}
                  className="btn btn-ghost btn-xs sm:btn-sm text-white hover:bg-white/20"
                  disabled={zoomLevel >= 3}
                >
                  <span className="hidden sm:inline">🔍+</span>
                  <span className="sm:hidden">+</span>
                </button>

                {/* Кнопка сброса зума */}
                <button
                  onClick={() => {
                    setZoomLevel(1);
                    setPanPosition({ x: 0, y: 0 });
                  }}
                  className="btn btn-ghost btn-xs sm:btn-sm text-white hover:bg-white/20"
                >
                  <span className="hidden sm:inline">🔄</span>
                  <span className="sm:hidden">0</span>
                </button>

                {/* Кнопка скачивания */}
                <button
                  onClick={() => {
                    const currentPicture = currentDealPictures[currentImageIndex];
                    const link = document.createElement('a');
                    link.href = `/api/v1/pictures/${currentPicture.pictureId}`;
                    link.download = currentPicture.name || `image_${currentPicture.pictureId}`;
                    link.click();
                  }}
                  className="btn btn-primary btn-xs sm:btn-sm"
                >
                  <span className="hidden sm:inline">📥 Скачать</span>
                  <span className="sm:hidden">📥</span>
                </button>

                {/* Кнопка закрытия */}
                <button
                  onClick={() => setGalleryOpen(false)}
                  className="btn btn-ghost btn-xs sm:btn-sm text-white hover:bg-white/20"
                >
                  ✕
                </button>
              </div>
            </div>
          </div>

          {/* Основная область с картинкой */}
          <div
            className="absolute inset-0 pt-12 pb-4 px-4 flex items-center justify-center overflow-hidden select-none"
            style={{
              userSelect: 'none',
              WebkitUserSelect: 'none',
              MozUserSelect: 'none',
              msUserSelect: 'none'
            }}
            onMouseDown={(e) => {
              if (zoomLevel > 1) {
                e.preventDefault();
                setIsDragging(true);
                setDragStart({ x: e.clientX - panPosition.x, y: e.clientY - panPosition.y });
              }
            }}

            onMouseMove={(e) => {
              if (isDragging && zoomLevel > 1) {
                e.preventDefault();
                const newX = e.clientX - dragStart.x;
                const newY = e.clientY - dragStart.y;
                setPanPosition({ x: newX, y: newY });
              }
            }}
            onMouseUp={(e) => {
              e.preventDefault();
              setIsDragging(false);
            }}
            onMouseLeave={(e) => {
              e.preventDefault();
              setIsDragging(false);
            }}
            onTouchStart={(e) => {
              if (zoomLevel > 1 && e.touches.length === 1) {
                const touch = e.touches[0];
                setIsDragging(true);
                setDragStart({ x: touch.clientX - panPosition.x, y: touch.clientY - panPosition.y });
              }
            }}
            onTouchMove={(e) => {
              if (isDragging && zoomLevel > 1 && e.touches.length === 1) {
                e.preventDefault();
                const touch = e.touches[0];
                setPanPosition({
                  x: touch.clientX - dragStart.x,
                  y: touch.clientY - dragStart.y
                });
              }
            }}
            onTouchEnd={() => setIsDragging(false)}
          >
            <img
              src={`/api/v1/pictures/${currentDealPictures[currentImageIndex]?.pictureId}`}
              alt={currentDealPictures[currentImageIndex]?.name || `Картинка ${currentImageIndex + 1}`}
              className="max-w-full max-h-full object-contain select-none"
              style={{
                transform: `scale(${zoomLevel}) translate(${panPosition.x / zoomLevel}px, ${panPosition.y / zoomLevel}px)`,
                cursor: zoomLevel > 1 ? (isDragging ? 'grabbing' : 'grab') : 'default',
                userSelect: 'none',
                WebkitUserSelect: 'none',
                MozUserSelect: 'none',
                msUserSelect: 'none'
              }}
              onError={(e) => {
                const target = e.target as HTMLImageElement;
                target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgdmlld0JveD0iMCAwIDIwMCAyMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIyMDAiIGhlaWdodD0iMjAwIiBmaWxsPSIjRjNGNEY2Ii8+CjxwYXRoIGQ9Ik02MCA4MEgxNDBWMTIwSDYwVjgwWiIgZmlsbD0iIzlDQTBBNiIvPgo8cGF0aCBkPSJNNjAgNjBIMTQwVjgwSDYwVjYwWiIgZmlsbD0iIzlDQTBBNiIvPgo8L3N2Zz4K';
              }}
            />
          </div>

          {/* Навигационные кнопки */}
          {currentDealPictures.length > 1 && (
            <>
              {/* Кнопка "Предыдущая" */}
              <button
                onClick={() => {
                  setCurrentImageIndex(prev =>
                    prev === 0 ? currentDealPictures.length - 1 : prev - 1
                  );
                  setZoomLevel(1);
                  setPanPosition({ x: 0, y: 0 });
                }}
                className="absolute left-2 sm:left-4 top-1/2 -translate-y-1/2 z-10 btn btn-circle btn-md sm:btn-lg bg-black/50 hover:bg-black/70 text-white border-0"
              >
                ‹
              </button>

              {/* Кнопка "Следующая" */}
              <button
                onClick={() => {
                  setCurrentImageIndex(prev =>
                    prev === currentDealPictures.length - 1 ? 0 : prev + 1
                  );
                  setZoomLevel(1);
                  setPanPosition({ x: 0, y: 0 });
                }}
                className="absolute right-2 sm:right-4 top-1/2 -translate-y-1/2 z-10 btn btn-circle btn-md sm:btn-lg bg-black/50 hover:bg-black/70 text-white border-0"
              >
                ›
              </button>
            </>
          )}

          {/* Миниатюры внизу */}
          {currentDealPictures.length > 1 && (
            <div className="absolute bottom-2 sm:bottom-4 left-1/2 -translate-x-1/2 z-10">
              <div className="flex gap-1 sm:gap-2 bg-black/50 backdrop-blur-sm rounded-lg p-1 sm:p-2 max-w-[calc(100vw-2rem)] overflow-x-auto">
                {currentDealPictures.map((picture, index) => (
                  <button
                    key={picture.id}
                    onClick={() => {
                      setCurrentImageIndex(index);
                      setZoomLevel(1);
                      setPanPosition({ x: 0, y: 0 });
                    }}
                    className={`w-12 h-12 sm:w-16 sm:h-16 rounded-lg overflow-hidden border-2 transition-all duration-200 flex-shrink-0 ${index === currentImageIndex
                        ? 'border-primary scale-110'
                        : 'border-white/30 hover:border-white/50'
                      }`}
                  >
                    <img
                      src={`/api/v1/pictures/${picture.pictureId}`}
                      alt={picture.name || `Миниатюра ${index + 1}`}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgdmlld0JveD0iMCAwIDIwMCAyMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIyMDAiIGhlaWdodD0iMjAwIiBmaWxsPSIjRjNGNEY2Ii8+CjxwYXRoIGQ9Ik02MCA4MEgxNDBWMTIwSDYwVjgwWiIgZmlsbD0iIzlDQTBBNiIvPgo8cGF0aCBkPSJNNjAgNjBIMTQwVjgwSDYwVjYwWiIgZmlsbD0iIzlDQTBBNiIvPgo8L3N2Zz4K';
                      }}
                    />
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Горячие клавиши */}
          <div className="absolute bottom-2 sm:bottom-4 right-2 sm:right-4 z-10 text-white/50 text-xs hidden sm:block">
            <div>← → для навигации</div>
            <div>🔍+ 🔍- для зума</div>
            <div>ESC для закрытия</div>
          </div>
        </div>
      )}

      {/* Модальное окно отправки заявки */}
      {submitModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-base-100 rounded-lg shadow-xl w-full max-w-md">
            <div className="p-6">
              <h3 className="text-xl font-bold text-base-content mb-4">
                Отправить заявку в CRM
              </h3>
              
              {/* Результат отправки */}
              {submitResult && (
                <div className={`alert mb-4 ${submitResult.success ? 'alert-success' : 'alert-error'}`}>
                  <div className="flex items-center">
                    {submitResult.success ? (
                      <span className="text-xl">✅</span>
                    ) : (
                      <span className="text-xl">❌</span>
                    )}
                    <span className="ml-2">{submitResult.message}</span>
                  </div>
                </div>
              )}
              
              {/* Форма отправки */}
              {!submitResult && (
                <div className="space-y-4">
                  {/* Имя */}
                  <div>
                    <label className="block text-sm font-medium text-base-content mb-2">
                      Имя <span className="text-error">*</span>
                    </label>
                    <input
                      type="text"
                      value={submitForm.name}
                      onChange={(e) => setSubmitForm(prev => ({ ...prev, name: e.target.value }))}
                      placeholder="Например: Иван"
                      className="input input-bordered w-full"
                    />
                  </div>

                  {/* Телефон */}
                  <div>
                    <label className="block text-sm font-medium text-base-content mb-2">
                      Телефон <span className="text-error">*</span>
                    </label>
                    <input
                      type="tel"
                      value={submitForm.phone}
                      onChange={(e) => handlePhoneChange(e.target.value)}
                      onInput={handlePhoneInput}
                      onKeyDown={handlePhoneKeyDown}
                      placeholder="+7 (999) 999-99-99"
                      className="input input-bordered w-full"
                      maxLength={20}
                      inputMode="tel"
                    />
                  </div>

                  {/* Адрес */}
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-base-content mb-2">
                        Улица <span className="text-error">*</span>
                      </label>
                      <input
                        type="text"
                        value={submitForm.street}
                        onChange={(e) => setSubmitForm(prev => ({ ...prev, street: e.target.value }))}
                        placeholder="Например: Пушкина"
                        className="input input-bordered w-full"
                      />
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-base-content mb-2">
                          Дом <span className="text-error">*</span>
                        </label>
                        <input
                          type="text"
                          value={submitForm.house}
                          onChange={(e) => setSubmitForm(prev => ({ ...prev, house: e.target.value }))}
                          placeholder="Например: 10к2"
                          className="input input-bordered w-full"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-base-content mb-2">
                          Квартира
                        </label>
                        <input
                          type="text"
                          value={submitForm.apartment}
                          onChange={(e) => setSubmitForm(prev => ({ ...prev, apartment: e.target.value }))}
                          placeholder="Например: 45"
                          className="input input-bordered w-full"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Комментарий */}
                  <div>
                    <label className="block text-sm font-medium text-base-content mb-2">
                      Комментарий
                    </label>
                    <textarea
                      value={submitForm.comment}
                      onChange={(e) => setSubmitForm(prev => ({ ...prev, comment: e.target.value }))}
                      placeholder="Дополнительная информация (необязательно)"
                      className="textarea textarea-bordered w-full h-24 resize-none"
                    />
                  </div>
                </div>
              )}

              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={handleCloseModal}
                  className="btn btn-ghost"
                  disabled={submitting}
                >
                  {submitResult ? 'Закрыть' : 'Отмена'}
                </button>
                {!submitResult && (
                  <button
                    onClick={handleSubmitDeal}
                    disabled={
                      submitting ||
                      !submitForm.name.trim() ||
                      !submitForm.phone ||
                      !submitForm.street.trim() ||
                      !submitForm.house.trim()
                    }
                    className="btn btn-primary"
                  >
                    {submitting ? (
                      <span className="loading loading-spinner loading-sm"></span>
                    ) : (
                      'Отправить заявку'
                    )}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default ClientPanel;
